import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SettingsOverviewSubpage.module.scss';
import { Checkbox, Grid, GridColumn, GridRow, Select, SelectOptionType } from '@bp/ui-components';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { BookingsModal } from '../../../components/Bookings/modals/BookingsModal';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { BpCard } from '../../../components/BpCard/BpCard';
import { adminRolesAsString } from '../../../utils/roleAsString';
import { OrganizationConfigContext } from '../../../context/OrganizationConfigContext';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { LicensedProductTypes } from './types';
import { BookingsTable } from 'components/Bookings/tables/BookingsTable';
import { graphql } from 'client/gql';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';

const UpdateBildungsplattformsInSettingsMutationDocument = graphql(`
  mutation UpdateBildungsplattformsInSettings($update: BildungsplattformUpdateInput!, $where: BildungsplattformWhere!) {
    updateBildungsplattforms(update: $update, where: $where) {
      bildungsplattforms {
        uuid
        __typename
        allowStudentDirectMessages
        currentSchoolyear
      }
    }
  }
`);

const SettingsOverviewSubpageQueryDocument = graphql(`
  query SettingsOverviewSubpage($profile: ID!, $organizationUuid: ID!) {
    adminProfiles(where: { organization: { uuid: $organizationUuid } }) {
      uuid
      __typename
      displayName
      organizationRoles
    }
    ...PermissionCheckerFields
    ...BookingsTableFields
  }
`);

export const SettingsOverviewSubpage = () => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data }] = useQuery({
    query: SettingsOverviewSubpageQueryDocument,
    variables: {
      organizationUuid: organizationUuid,
      profile: profileUuid,
    },
  });

  const permissions = usePermissionChecker(data);

  const [, updateBildungsplattform] = useMutation(UpdateBildungsplattformsInSettingsMutationDocument);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalType, setModalType] = useState<LicensedProductTypes | null>(null);

  const {
    organizationProducts,
    schoolyears,
    currentSchoolYear,
    setCurrentSchoolYear,
    allowStudentDirectMessages,
    setAllowStudentDirectMessages,
  } = useContext(OrganizationConfigContext);

  const bildungsplattform = organizationProducts?.find((p) => p.__typename === 'Bildungsplattform');

  const schoolyearSelectOptions = useCreateSelectOptions(schoolyears, 'uuid', 'name');

  const handleSelectSchoolyear = async (ev: SelectOptionType) => {
    const selectedSchoolyear = schoolyears?.find((s) => s.uuid === ev.value);
    if (selectedSchoolyear !== undefined) {
      if (bildungsplattform) {
        const resp = await updateBildungsplattform({
          update: {
            currentSchoolyear: selectedSchoolyear.uuid,
          },
          where: { uuid: bildungsplattform.uuid },
        });
        if (resp.error) {
          showErrorToast(resp.error);
        } else {
          setCurrentSchoolYear(selectedSchoolyear);
          showSuccessToast(t('common.saved'));
        }
      }
    }
  };

  const handleAllowStudentDirectMessages = async (value: boolean) => {
    if (bildungsplattform) {
      const resp = await updateBildungsplattform({
        update: {
          allowStudentDirectMessages: value,
        },
        where: { uuid: bildungsplattform.uuid },
      });
      if (resp.error) {
        showErrorToast(resp.error);
      } else {
        setAllowStudentDirectMessages(value);
        showSuccessToast(t('common.saved'));
      }
    }
  };

  const handleEdit = (licensedProduct: LicensedProductTypes) => {
    setModalType(licensedProduct);
    setModalOpen(true);
  };

  return (
    <BpSubpage className={styles['settings-overview']}>
      <Grid>
        <GridRow mobileGap='var(--grid-column-gap)' className={styles.top}>
          <GridColumn width={9}>
            <BpCard header={{ headline: t('common.administration') }}>
              <div className={styles['admins-list']}>
                {data?.adminProfiles.map((p) => {
                  return (
                    <div key={p.uuid} className={styles.admin}>
                      <div className={styles.name}>{p.displayName}</div>
                      <div className={styles.roles}>
                        {p.organizationRoles.map(
                          (role) =>
                            role && adminRolesAsString(role) && <div key={role}>{adminRolesAsString(role)}</div>,
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </BpCard>
          </GridColumn>
          <GridColumn width={3}>
            <BpCard header={{ headline: t('settings.title') }} showOverflow>
              <Select
                options={schoolyearSelectOptions}
                onChange={async (e) => {
                  await handleSelectSchoolyear(e as unknown as SelectOptionType);
                }}
                defaultValue={schoolyearSelectOptions.find((opt) => currentSchoolYear?.uuid === (opt.value as string))}
                name={'currentSchoolyear'}
                disabled={!permissions?.isOrganizationAdmin(pimAuthClaims.getOrganization())}
                label={t('common.schoolyear')}
              />
              <Checkbox
                name={'allowStudentDirectMessages-direct'}
                checked={allowStudentDirectMessages}
                onChange={async () => {
                  await handleAllowStudentDirectMessages(!allowStudentDirectMessages);
                }}
                disabled={!permissions?.isOrganizationAdmin(pimAuthClaims.getOrganization())}
                label={t('institution.allowStudentDirectMessages')}
              />
            </BpCard>
          </GridColumn>
        </GridRow>
        <GridRow>
          <BpCard noPadding header={{ headline: t('institution.actualBookings') }}>
            <BookingsTable onEdit={handleEdit} query={data} />
          </BpCard>
        </GridRow>
      </Grid>

      {modalType && (
        <BookingsModal
          bookingType={modalType}
          isOpen={modalOpen}
          onClose={() => {
            setModalType(null);
            setModalOpen(false);
          }}
        />
      )}
    </BpSubpage>
  );
};
